<template>
  <MainLayout :title="trans('Form')">
    <div
      class="sticky top-0 z-10 bg-theme-background"
      style="padding-top: var(--safe-area-inset-top)"
    >
      <div class="flex items-center p-4">
        <ADeepCircleButton
          class="flex-shrink-0"
          @click="router.push({ name: 'home' })"
        >
          <template #icon><ArrowLeftBold /></template>
        </ADeepCircleButton>
        <div
          class="flex-1 truncate px-4 text-center font-primary text-lg font-medium"
        >
          {{ localize(form.title) }}
        </div>
        <div class="h-10 w-10 flex-shrink-0"></div>
      </div>
    </div>
    <div class="p-4">
      <div v-if="!submitted" class="">
        <div
          v-if="answers.some((answer) => answer.question.isMandatory)"
          class="mb-8 text-xs"
        >
          <span class="text-red-500">* </span>
          <span>{{ trans('These fields are required') }}</span>
        </div>
        <form @submit.prevent="submit">
          <div
            v-for="(answer, answerIndex) in answers"
            :key="answerIndex"
            class="mb-6"
          >
            <div>
              {{ localize(answer.question.question) }}
              <span v-if="answer.question.isMandatory" class="text-red-500"
                >*</span
              >
            </div>

            <div class="mt-2">
              <template v-if="answer.question.type === 'text'">
                <textarea
                  v-model="answer.answer"
                  rows="3"
                  class="menu-form-input w-full"
                  :required="answer.question.isMandatory"
                />
              </template>

              <template v-if="answer.question.type === 'rating'">
                <ARating v-model="answer.answer as number" />
              </template>
            </div>
          </div>

          <button
            type="submit"
            class="menu-button-primary w-max"
            :disabled="!isValid || submitting"
          >
            {{ submitting ? `${trans('Wait')}...` : trans('Submit') }}
          </button>
        </form>
      </div>
      <div v-else class="text-center">
        {{ trans('Thanks') }}
      </div>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@menu/Shared/MainLayout.vue';
import ADeepCircleButton from '@menu/Shared/ADeepCircleButton.vue';
import ARating from '@menu/Shared/ARating.vue';
import axios from 'axios';
import ArrowLeftBold from '@/phosphoricons/ArrowLeftBold.vue';
import { localize, trans } from '@menu/Utils/i18n';
import { computed, ref, toValue } from 'vue';
import { FormQuestionResource } from '@menu/Types/Resources/FormQuestionResource';

import { FormQuestionType } from '@menu/Types/Enums/FormQuestionType';
import { useRouter } from 'vue-router';
import getRestaurant from '@menu/Utils/getRestaurant';

const router = useRouter();

const restaurant = getRestaurant();

const form = ref(restaurant.forms[0]);

const submitting = ref(false);
const submitted = ref(false);

type Answer = {
  question: FormQuestionResource;
  answer: number | string | null;
};

const answers = ref<Answer[]>(
  toValue(form).questions.map((question) => ({
    question,
    answer: (() => {
      if (question.type === 'rating') {
        return 0;
      }
      if (question.type === 'text') {
        return '';
      }
      return null;
    })(),
  })),
);

function isAnswerEmpty(answer: Answer) {
  if (answer.question.type === FormQuestionType.RATING && answer.answer === 0) {
    return true;
  }

  if (answer.question.type === FormQuestionType.TEXT && answer.answer === '') {
    return true;
  }

  return false;
}

const isValid = computed(() => {
  return !answers.value.some((answer) => {
    if (!answer.question.isMandatory) {
      return false;
    }

    return isAnswerEmpty(answer);
  });
});

const submit = () => {
  if (!isValid.value) {
    return;
  }

  submitting.value = true;

  axios
    .post('/api/form-submissions', {
      restaurantId: restaurant.id,
      formId: form.value.id,
      answers: answers.value.map((answer) => ({
        questionId: answer.question.id,
        answer: isAnswerEmpty(answer) ? null : answer.answer,
      })),
    })
    .finally(() => {
      submitted.value = true;
      submitting.value = false;
    });
};
</script>
